/**
 * SafeRolloutPanel (J2-I2) — the safe-rollout monitoring surface mounted at
 * the bottom of the Launch Readiness page.
 *
 * Lists the project's safe rollouts newest-first (active ones expanded). Per
 * rollout: "Safe rollout — {versionId} → live" header + status badge, the
 * ALWAYS-visible J2-PRE-003 disclosure, one scope/summary line, guardrail
 * rows (frozen thresholds merged with the LATEST look per guardrail), a loud
 * regression alert naming the breaching guardrail with a one-click
 * ROLL BACK button, and EVALUATE NOW while monitoring.
 *
 * Composition per .design/journey-program-wave1/mockups/safe-rollout-panel.html.
 * All copy lives in utils/safeRollout.ts (banned-grammar swept).
 */

import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LAUNCH_READINESS_TEST_IDS } from '../testIds';
import { useSafeRollouts } from '../hooks/useSafeRollouts';
import {
  SAFE_ROLLOUT_STATUS_BADGES,
  SAFE_ROLLOUT_UI_COPY,
  buildScopeLine,
  crossedGuardrailLabels,
  isActiveSafeRollout,
  mergeGuardrailsWithLooks,
} from '../utils/safeRollout';
import type { SafeRolloutGuardrailRow } from '../utils/safeRollout';
import type { SafeRolloutRecord } from '../types/safeRollout.types';

export interface SafeRolloutPanelProps {
  projectId: string | undefined;
}

/** One guardrail line: ✓ within bound / ✕ crossed (with observation count). */
function GuardrailRow({ row }: { row: SafeRolloutGuardrailRow }) {
  const detail = row.crossed
    ? SAFE_ROLLOUT_UI_COPY.crossedRowDetail({
      deltaPp: row.deltaPp,
      observations: row.latestLook?.observations ?? 0,
    })
    : row.latestLook
      ? SAFE_ROLLOUT_UI_COPY.withinBound
      : SAFE_ROLLOUT_UI_COPY.noLookYet;

  return (
    <Box
      data-testid={LAUNCH_READINESS_TEST_IDS.safeRolloutGuardrail(row.key)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {row.crossed
        ? <CloseIcon color="error" sx={{ fontSize: 18, flex: 'none' }} />
        : row.latestLook
          ? <CheckIcon color="success" sx={{ fontSize: 18, flex: 'none' }} />
          : (
            <Box component="span" sx={{ width: 18, textAlign: 'center', color: 'text.secondary', flex: 'none' }}>
              —
            </Box>
          )}
      <Typography
        variant="body2"
        sx={row.crossed ? { color: 'error.main', fontWeight: 500 } : undefined}
      >
        {row.crossed ? SAFE_ROLLOUT_UI_COPY.crossedRowTitle(row.label) : row.label}
      </Typography>
      <Typography variant="caption" color={row.crossed ? 'error.main' : 'text.secondary'} sx={{ ml: 'auto', flex: 'none' }}>
        {detail}
      </Typography>
    </Box>
  );
}

interface SafeRolloutCardProps {
  record: SafeRolloutRecord;
  expanded: boolean;
  busy: boolean;
  onToggle: () => void;
  onEvaluate: () => void;
  onRollback: () => void;
}

function SafeRolloutCard({ record, expanded, busy, onToggle, onEvaluate, onRollback }: SafeRolloutCardProps) {
  const badge = SAFE_ROLLOUT_STATUS_BADGES[record.status] ?? SAFE_ROLLOUT_STATUS_BADGES.cancelled;
  const rows = mergeGuardrailsWithLooks(record.thresholds.guardrails, record.looks);
  const crossedLabels = crossedGuardrailLabels(rows);

  return (
    <Paper sx={{ px: 2.5, py: 2, mb: 2 }}>
      {/* Header: title + status badge + expand toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {SAFE_ROLLOUT_UI_COPY.title(record.scope.versionId)}
        </Typography>
        <Chip
          data-testid={LAUNCH_READINESS_TEST_IDS.safeRolloutStatus}
          label={badge.label}
          color={badge.color}
          variant={badge.variant}
          size="small"
          sx={{ textTransform: 'uppercase', fontWeight: 500, flex: 'none' }}
        />
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse safe rollout' : 'Expand safe rollout'}
          onClick={onToggle}
          sx={{ ml: 'auto', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* J2-PRE-003 disclosure — ALWAYS visible; green adds the non-claim. */}
      <Typography
        data-testid={LAUNCH_READINESS_TEST_IDS.safeRolloutDisclosure}
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ mb: 1 }}
      >
        {SAFE_ROLLOUT_UI_COPY.disclosure}
        {record.status === 'no_regression'
          ? ` ${SAFE_ROLLOUT_UI_COPY.greenQualifier(record.scope.versionId)}`
          : ''}
      </Typography>

      {/* Scope / summary line */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: expanded ? 1 : 0 }}>
        {buildScopeLine(record, Date.now())}
      </Typography>

      {/* Loud regression alert naming the breaching guardrail(s) */}
      {record.status === 'regression_detected' && (
        <Alert
          data-testid={LAUNCH_READINESS_TEST_IDS.safeRolloutAlert}
          severity="error"
          sx={{ my: 1 }}
        >
          <AlertTitle>{SAFE_ROLLOUT_UI_COPY.alertTitle}</AlertTitle>
          {SAFE_ROLLOUT_UI_COPY.alertBody(
            crossedLabels.length > 0 ? crossedLabels : rows.map((row) => row.label),
          )}
        </Alert>
      )}

      {/* Guardrail rows: frozen thresholds × latest look per guardrail */}
      {expanded && (
        <Box>
          {rows.map((row) => (
            <GuardrailRow key={row.key} row={row} />
          ))}
        </Box>
      )}

      {/* Actions: one-click rollback on regression; manual look while monitoring */}
      {record.status === 'regression_detected' && (
        <Button
          data-testid={LAUNCH_READINESS_TEST_IDS.safeRolloutRollback}
          variant="contained"
          color="error"
          size="small"
          disabled={busy}
          onClick={onRollback}
          sx={{ mt: 1.5 }}
        >
          {SAFE_ROLLOUT_UI_COPY.rollbackButton(record.scope.previousVersionId)}
        </Button>
      )}
      {record.status === 'monitoring' && (
        <Button
          data-testid={LAUNCH_READINESS_TEST_IDS.safeRolloutEvaluate}
          variant="outlined"
          size="small"
          disabled={busy}
          onClick={onEvaluate}
          sx={{ mt: 1.5 }}
        >
          {SAFE_ROLLOUT_UI_COPY.evaluateButton}
        </Button>
      )}
    </Paper>
  );
}

export const SafeRolloutPanel: React.FC<SafeRolloutPanelProps> = ({ projectId }) => {
  const { rollouts, loading, error, busyId, actionError, refetch, evaluate, rollback } =
    useSafeRollouts(projectId);

  // Expanded state: user overrides win; default = active rollouts expanded.
  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({});
  const isExpanded = (record: SafeRolloutRecord): boolean =>
    expandedOverrides[record.id] ?? isActiveSafeRollout(record);
  const toggle = (record: SafeRolloutRecord) =>
    setExpandedOverrides((prev) => ({ ...prev, [record.id]: !isExpanded(record) }));

  return (
    <Box data-testid={LAUNCH_READINESS_TEST_IDS.safeRolloutPanel}>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        {SAFE_ROLLOUT_UI_COPY.sectionTitle}
      </Typography>

      {loading && <Skeleton variant="rounded" height={96} />}

      {!loading && error && (
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={() => { void refetch(); }}>
              Retry
            </Button>
          )}
        >
          <AlertTitle>{SAFE_ROLLOUT_UI_COPY.loadErrorTitle}</AlertTitle>
          {error.message}
        </Alert>
      )}

      {!loading && !error && actionError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {actionError.message}
        </Alert>
      )}

      {!loading && !error && rollouts.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {SAFE_ROLLOUT_UI_COPY.emptyState}
        </Typography>
      )}

      {!loading && !error && rollouts.map((record) => (
        <SafeRolloutCard
          key={record.id}
          record={record}
          expanded={isExpanded(record)}
          busy={busyId !== null}
          onToggle={() => toggle(record)}
          onEvaluate={() => { void evaluate(record.id); }}
          onRollback={() => { void rollback(record.id); }}
        />
      ))}
    </Box>
  );
};

export default SafeRolloutPanel;
